import * as signalR from '@microsoft/signalr';
import { Router } from '@angular/router';
import { UserInfo } from '../model/userInfo';
import { Message } from "../model/message";
import { GpsLocation } from "../model/gpslocation";
import { WorkShiftGroup } from "../model/workShiftGroup";
import { LiveFlightSyncStatus } from "../model/liveflight-sync";
import { GateStatus } from "../model/GateStatus";
import { SettingsService } from './settings.service';
import { LiveFlight } from '../model/live-flight';
import { Actor } from '../model/actor';
import { ActivityActor } from '../model/activityActor';
import * as json from '../common/json';
import { AppConfig } from "../model/config";
import { EventEmitter, Injectable, NgZone } from '@angular/core';
import { UserImportLog } from '../model/userImportLog';
import { UserCheckIn } from '../model/userCheckIn';
import { AuthService } from './auth.service';
import { DispatchStatus } from '../dispatcher/dispatcher.component';


@Injectable()
export class SignalRService {

  private hubConnection: signalR.HubConnection;
  private currentAirport: string;
  public hubConnectionStatusIcon: string = "cloud_off";
  public hubConnectionStatusText: string = "";

  public login$: EventEmitter<UserInfo>;
  public logout$: EventEmitter<UserInfo>;

  public deleteActor$: EventEmitter<Actor>;
  public updateActor$: EventEmitter<Actor>;

  public deleteLiveFlights$: EventEmitter<LiveFlight[]>;
  public updateLiveFlights$: EventEmitter<LiveFlight[]>;

  public deleteActivity$: EventEmitter<ActivityActor>;
  public updateActivity$: EventEmitter<ActivityActor>;

  public updateWorkShiftGroups$: EventEmitter<WorkShiftGroup[]>;

  public deleteMessage$: EventEmitter<Message>;
  public updateMessage$: EventEmitter<Message>;
  public reconnectedEvent$: EventEmitter<boolean>;

  public checkedin$: EventEmitter<UserCheckIn>;
  public checkedout$: EventEmitter<UserCheckIn>;

  public gatePing$: EventEmitter<string>;
  public updateGateStatus$: EventEmitter<GateStatus[]>;

  public updateGpsLocation$: EventEmitter<GpsLocation>;

  public lastLiveFlightSync$: EventEmitter<LiveFlightSyncStatus>;

  public updateImportLog$: EventEmitter<UserImportLog>;

  public updateDispatchStatus$: EventEmitter<DispatchStatus>;

  constructor(
    private readonly authService: AuthService,
    private readonly zone: NgZone,
    private readonly router: Router,
    private readonly settings: SettingsService,
    private readonly configuration: AppConfig) {

    this.login$ = new EventEmitter<UserInfo>();
    this.logout$ = new EventEmitter<UserInfo>();

    this.checkedin$ = new EventEmitter<UserCheckIn>();
    this.checkedout$ = new EventEmitter<UserCheckIn>();

    this.deleteLiveFlights$ = new EventEmitter<LiveFlight[]>();
    this.updateLiveFlights$ = new EventEmitter<LiveFlight[]>();

    this.deleteActor$ = new EventEmitter<Actor>();
    this.updateActor$ = new EventEmitter<Actor>();

    this.deleteActivity$ = new EventEmitter<ActivityActor>();
    this.updateActivity$ = new EventEmitter<ActivityActor>();

    this.updateWorkShiftGroups$ = new EventEmitter<WorkShiftGroup[]>();

    this.deleteMessage$ = new EventEmitter<Message>();
    this.updateMessage$ = new EventEmitter<Message>();

    this.gatePing$ = new EventEmitter<string>();
    this.updateGateStatus$ = new EventEmitter<GateStatus[]>();

    this.updateGpsLocation$ = new EventEmitter<GpsLocation>();

    this.lastLiveFlightSync$ = new EventEmitter<LiveFlightSyncStatus>();
    this.updateImportLog$ = new EventEmitter<UserImportLog>();

    this.reconnectedEvent$ = new EventEmitter<boolean>();

    this.updateDispatchStatus$ = new EventEmitter<DispatchStatus>();
  }

  start(currentAirport: string): void {
    this.currentAirport = currentAirport;
    this.initializeSignalRConnection();
  }

  private async joinGroup(airport: string): Promise<boolean> {
    try {
      await this.hubConnection.invoke("JoinGroup", airport);
      this.currentAirport = airport;
      this.logInfo("Joined airport:", this.currentAirport);
      return true;
    } catch (error) {
      this.logInfo(`Failed to join airport. Error: ${error}`);
      return false;
    }
  }

  private logInfo(message: string, data?: any) {
    const time = new Date().toLocaleTimeString();
    let printInfo = '[' + time + ' SignalR] ' + message;
    if (data !== undefined) {
      printInfo += ' ' + JSON.stringify(data);
    }
    console.log(printInfo);
  }

  private changeStateInfo(icon: string, text: string) {
    this.zone.run(() => {
      this.hubConnectionStatusIcon = icon;
      this.hubConnectionStatusText = text;
    });
  }

  public initializeSignalRConnection(): void {
    let self = this;

    this.authService.acquireToken().subscribe(token => {

      let signalRHub = this.configuration.backendUrl + "/signalr";
      let queryString = "?WindowStart=" +
        self.settings.windowStartOffset +
        "&WindowEnd=" +
        self.settings.windowEndOffset +
        "&Airport=" +
        self.currentAirport;


      self.hubConnection = new signalR.HubConnectionBuilder()
        .configureLogging(signalR.LogLevel.Information)
        .withUrl(signalRHub + queryString, { accessTokenFactory: () => token.accessToken })
        .withAutomaticReconnect()
        .build();

      self.hubConnection.keepAliveIntervalInMilliseconds = 15000;
      self.hubConnection.serverTimeoutInMilliseconds = 120000;

      self.setupHubCallbacks();
      self.hubConnection.start().then(() => {
        if (this.hubConnection.state == signalR.HubConnectionState.Connected) {
          self.logInfo('SignalR Connected!');
          this.changeStateInfo("cloud_done", "Connected");

          self.joinGroup(this.currentAirport);
        }
      }).catch(err => console.error(JSON.stringify(err)));
    });
  }

  private setupHubCallbacks() {


    //ONCLOSE
    this.hubConnection.onclose((error) => {
      this.changeStateInfo("cloud_off", "Not connected");
      this.logInfo('SignalR onclose...', error?.message);

      this.zone.run(() => this.router.navigate(["/signalrerror"]));
    });

    //Reconnecting
    this.hubConnection.onreconnecting(err => { 
      this.changeStateInfo("cloud_queue", "Connecting");
      this.logInfo('SignalR Reconnecting...', err?.message);
    });

    //Reconnected
    this.hubConnection.onreconnected(async () => {
      this.changeStateInfo("cloud_done", "Connected");
      this.logInfo('SignalR Reconnected!');
      if (await this.joinGroup(this.currentAirport)) {
        this.zone.run(() => this.reconnectedEvent$.emit(true));
      }
    });

    // login
    this.hubConnection.on('login', (data: string) => {
      let userInfo = JSON.parse(data) as UserInfo;
      this.zone.run(() => { this.login$.emit(userInfo) });
    });

    // logout
    this.hubConnection.on('logout', (data: string) => {
      let userInfo = JSON.parse(data) as UserInfo;
      this.zone.run(() => { this.logout$.emit(userInfo) });
    });

    // checkIn
    this.hubConnection.on('checkin', (userCheckIn: UserCheckIn) => {
      console.log("Checkin: " + userCheckIn.usermappingId);
      this.checkedin$.next(userCheckIn);
    });

    // checkOut
    this.hubConnection.on('checkout', (userCheckIn: UserCheckIn) => {
      console.log("Checkout: " + userCheckIn.usermappingId)
      this.checkedout$.next(userCheckIn);

    });

    //Message
    this.hubConnection.on('deleteMessage', (data: string) => {
      let message = JSON.parse(data) as Message;
      this.deleteMessage$.emit(message);
    });

    this.hubConnection.on('updateMessage', (data: string) => {
      let message = JSON.parse(data) as Message;
      this.updateMessage$.emit(message);
    });

    this.hubConnection.on('updateGpsLocation', (data: string) => {
      let gps: GpsLocation = new GpsLocation(JSON.parse(data));
      this.zone.run(() => { this.updateGpsLocation$.emit(gps) });
    });

    this.hubConnection.on('updateWorkShiftGroups', (data: string) => {
      let groups = JSON.parse(data) as WorkShiftGroup[];
      this.updateWorkShiftGroups$.emit(groups);
      console.log("UpdateWorkShiftGroups");

    });


    this.hubConnection.on('lastLiveFlightSync', (time: Date, httpStatus: number, message: string) => {
      console.log("lastLiveFlightSync");
      this.lastLiveFlightSync$.emit(new LiveFlightSyncStatus(time, httpStatus, message));
    });

    this.hubConnection.on('notificationUpdate', (action: string, payload: string, payloadExtended: string) => {
      switch (action) {
        case "deleteActor":
          {
            let actor = JSON.parse(payload) as Actor;
            this.deleteActor$.emit(actor);
            break;
          }
        case "updateActor":
          {
            let actor = JSON.parse(payload) as Actor;
            this.updateActor$.emit(actor);
            break;
          }
        case "createActor":
          {
            console.log("Create actor should not be called!?");
            break;
          }
        case "createActivity":
          {
            console.log("Create activity should not be called!?");
            break;
          }
        case "deleteActivity":
          {
            console.log("delete Activity");
            let activity = JSON.parse(payload) as ActivityActor;
            this.deleteActivity$.emit(activity);
            break;
          }
        case "updateActivity":
          {
            let activity: ActivityActor = new ActivityActor(JSON.parse(payload, json.dateParser) as ActivityActor);
            this.updateActivity$.emit(activity);
            break;
          }
        case "createLiveFlights":
          {
            console.log("Create createLiveFlights should not be called!?");
            break;
          }
        case "updateLiveFlights":
          {
            let liveFlights = JSON.parse(payload) as LiveFlight[];
            this.updateLiveFlights$.emit(liveFlights);
            break;
          }
        case "deleteLiveFlights":
          {
            let liveFlights = JSON.parse(payload) as LiveFlight[];
            this.deleteLiveFlights$.emit(liveFlights);
            break;
          }
        default:
          {
            console.log("NotificationUpdate: Unknown action", action);
          }
      }

    });

    this.hubConnection.on('updateGateStatus', (message) => {
      let status: GateStatus[] = JSON.parse(message) as GateStatus[];
      this.updateGateStatus$.emit(status);
    });

    this.hubConnection.on('ping', (param) => {
      let gate = JSON.parse(param) as string;
      this.gatePing$.emit(gate);
    });

    this.hubConnection.on('updateImportLog', (data: string) => {

      let signalRUserImportLog: UserImportLog = JSON.parse(data) as UserImportLog;

      let importLog = new UserImportLog(
        signalRUserImportLog.id,
        signalRUserImportLog.dateKey,
        signalRUserImportLog.type,
        signalRUserImportLog.message,
        signalRUserImportLog.resolved,
        signalRUserImportLog.affectedResourceId,
        signalRUserImportLog.affectedResourceName,
        signalRUserImportLog.updatedByUserMappingId,
        signalRUserImportLog.updatedBy,
        signalRUserImportLog.airportId,
        new Date(signalRUserImportLog.createdAt)
      );

      this.updateImportLog$.emit(importLog);
    });

    this.hubConnection.on('updateDispatchStatus', (param) => {
      let status = JSON.parse(param) as DispatchStatus;
      this.updateDispatchStatus$.emit(status);
    });
  }
}
